<script setup lang="ts">
import { computed } from 'vue';
import { BFormCheckbox } from 'bootstrap-vue-next';
import { accepted as acceptedRule } from './schemas';
import {
  useItpFormControl,
  useItpFormField,
  useItpFormValidation,
} from './useItpForm';

const props = defineProps<{
  modelValue?: boolean | null;

  name: string;
  id?: string;
  size?: 'sm' | 'md' | 'lg';
  placeholder?: string;
  label?: string;

  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  autofocus?: boolean;

  inline?: boolean;
  indeterminate?: boolean;
  isSwitch?: boolean;
  plain?: boolean;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean | null): void;
  (
    event: 'change',
    payload: { checked: boolean | null; revert: () => void },
  ): void;
  (event: 'input', value: boolean | null): void;
}>();

const { isDisabled, isReadonly } = useItpFormControl(props);

const { touch } = useItpFormField(props, (isRequired) =>
  isRequired ? { accepted: acceptedRule } : {},
);

const { state } = useItpFormValidation(props.name);

const modelProxy = computed<any>({
  get() {
    return props.modelValue ?? false;
  },
  set(next: any) {
    const previous = props.modelValue ?? null;
    const normalized = next as boolean;
    emit('update:modelValue', normalized);
    emit('change', {
      checked: normalized,
      revert: () => emit('update:modelValue', previous),
    });
    emit('input', normalized);
    touch();
  },
});
</script>

<template>
  <div :class="inline ? 'form-check-inline mx-2 my-1' : ''">
    <BFormCheckbox
      :id="id || name"
      v-model="modelProxy"
      :name="name"
      :disabled="isDisabled || isReadonly"
      :required="required"
      :indeterminate="indeterminate"
      :plain="plain"
      :switch="isSwitch"
      :size="size"
      :state="state"
    >
      <slot>{{ label }}</slot>
    </BFormCheckbox>
  </div>
</template>
